import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { BillingPolicyAction } from './billing-policy-action';
import { BillingPolicyLogic } from './billing-policy-logic';
import { OfferBillingPolicy } from './offer-billing-policy';
import {
  OrderedSequenceRule,
  OrderedsequenceruleConfig,
} from './ordered-sequence-rule';
import { PricePointRule } from './price-point-rule';
import {
  NumberFilter,
  StringFilter,
  BooleanFilter,
  ListFilter,
} from 'src/app/filter/filter';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import { AtomService } from '../atom.service';
import { Country } from './country.resource';
import { Job } from './job.resource';
import { Period } from './period.resource';
import { Platform } from './platform.resource';
import {
  BillingPolicyActionConfig,
  CountryConfig,
  EndUserProviderJobConfig,
  PeriodConfig,
  PlatformConfig,
} from 'src/app/shared/tools/search/search-config';

@Resource({
  type: 'billingpolicies',
})
export class BillingPolicy extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute({ key: 'reset_tries' }) resetTries?: number;
  @Attribute({ key: 'reset_interval' }) resetInterval?: string;
  @Attribute({ key: 'cooldown_interval' }) cooldownInterval?: string;
  @Attribute({ key: 'grace_tries' }) graceTries?: number;
  @Attribute({ key: 'grace_interval' }) graceInterval?: string;
  @Attribute() mode?: 'realtime' | 'windows';
  @Attribute({
    key: 'charging_windows',
    convert: (value: string | undefined) => value?.split(','),
    revert: (value: undefined | number[]) =>
      value && value.length > 0 ? value.join(',') : null,
  })
  chargingWindows?: number[];
  @Attribute({ key: 'back_billing_interval' }) backBillingInterval?: string;
  @Attribute({ key: 'split_billing' }) splitBilling?: boolean;
  @Attribute() data?: Record<string, unknown>;
  @Attribute() validity?: string;
  @Attribute({ key: 'validity_tz' }) validityTz?: string;

  @Relationship() country?: Country;
  @Relationship() platform?: Platform;
  @Relationship() enduserproviderjob?: Job;
  @Relationship() period?: Period;
  @Relationship() logic?: BillingPolicyLogic;
  @Relationship() startpolicyaction?: BillingPolicyAction;
  @Relationship() resetstartaction?: BillingPolicyAction;
  @Relationship() newcycleaction?: BillingPolicyAction;
  @Relationship() endpolicyaction?: BillingPolicyAction;
  @Relationship() newmonthaction?: BillingPolicyAction;
  @Relationship() orderedsequencerules?: OrderedSequenceRule[];
  @Relationship() pricepointrules?: PricePointRule[];
  @Relationship() offerbillingpolicy?: OfferBillingPolicy[];
}

export class BillingpoliciesConfig implements ResourceConfig {
  tables = {
    includes: [
      'logic',
      'startpolicyaction',
      'resetstartaction',
      'newcycleaction',
      'endpolicyaction',
      'newmonthaction',
      'orderedsequencerules.currency',
      'pricepointrules.currency',
      'pricepointrules.onsuccess',
      'pricepointrules.nocredit',
      'country',
      'platform',
      'period',
      'enduserproviderjob.firm.headquarterscountry',
      'enduserproviderjob.activity',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Name',
        displayed: true,
        sort: 'name',
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'country.name',
        displayedDatas: [
          { path: ['country.name'], class: 'mat-body-2', translatable: true },
          { path: ['country.id'], class: 'mat-body-1', translatable: false },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
      },
      {
        columnDef: 'platform.name',
        displayedDatas: [
          { path: ['platform.name'], class: 'main', translatable: true },
        ],
        header: 'Platform',
        translatable: true,
        displayed: true,
        link: {
          resource: 'platforms',
          filter: 'platform.id',
          icon: 'dns',
        },
      },
      {
        columnDef: 'enduserproviderjob.firm.name',
        displayedDatas: [
          {
            path: [
              'enduserproviderjob.firm.name',
              'enduserproviderjob.firm.headquarterscountry.id',
            ],
            class: 'mat-body-2',
          },
          {
            path: ['enduserproviderjob.activity.name'],
            class: 'mat-body-1',
          },
        ],
        header: 'Enduser Provider',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'enduserproviderjob.firm.id',
          icon: 'signal_cellular_alt',
        },
      },
      {
        columnDef: 'validity',
        header: 'Validity',
        displayed: false,
      },
      {
        columnDef: 'period',
        displayedDatas: [
          {
            path: ['period.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Period',
        displayed: true,
        sort: 'period',
      },
      {
        columnDef: 'resetTries',
        displayedDatas: [
          {
            path: ['resetTries'],
            class: 'mat-body-2',
          },
        ],
        header: 'Reset Tries',
        displayed: false,
        sort: 'resetTries',
      },
      {
        columnDef: 'resetInterval',
        displayedDatas: [
          {
            path: ['resetInterval'],
            class: 'mat-body-2',
            type: 'interval',
          },
        ],
        header: 'Reset Interval',
        displayed: false,
        sort: 'reset_interval',
      },
      {
        columnDef: 'cooldownInterval',
        displayedDatas: [
          {
            path: ['cooldownInterval'],
            class: 'mat-body-2',
            type: 'interval',
          },
        ],
        header: 'Cooldown Interval',
        displayed: false,
        sort: 'cooldown_interval',
      },
      {
        columnDef: 'graceTries',
        displayedDatas: [
          {
            path: ['graceTries'],
            class: 'mat-body-2',
          },
        ],
        header: 'Grace Tries',
        displayed: false,
        sort: 'grace_tries',
      },
      {
        columnDef: 'graceInterval',
        displayedDatas: [
          {
            path: ['graceInterval'],
            class: 'mat-body-2',
            type: 'interval',
          },
        ],
        header: 'Grace Interval',
        displayed: true,
        sort: 'grace_interval',
      },
      {
        columnDef: 'mode',
        displayedDatas: [
          {
            path: ['mode'],
            class: 'mat-body-2',
          },
        ],
        header: 'mode',
        displayed: true,
        sort: 'mode',
      },
      {
        columnDef: 'chargingWindows',
        displayedDatas: [
          {
            path: [''],
            class: 'mat-body-2',
            type: 'chips',
          },
        ],
        header: 'Charging Windows',
        displayed: false,
        sort: 'charging_windows',
      },
      {
        columnDef: 'splitBilling',
        displayedDatas: [
          {
            path: ['splitBilling'],
            class: 'mat-body-2',
            type: 'boolean',
          },
        ],
        header: 'Split Billing ?',
        displayed: true,
        sort: 'split_billing',
      },
      {
        columnDef: 'offers',
        header: 'Offers',
        displayed: true,
        link: {
          resource: 'offers',
          list: 'offerbillingpolicy',
          target: 'offerbillingpolicy.billingpolicy',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'shield',
      title: [
        {
          path: ['name'],
          translatable: true,
        },
      ],
      id: true,
      validity: true,
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'call_split',
            title: 'Split Billing',
            APIKey: 'splitBilling',
            value: {
              path: ['splitBilling'],
              type: 'boolean',
            },
          },
          {
            icon: 'arrow_right',
            title: 'Mode',
            APIKey: 'mode',
            value: {
              path: ['mode'],
            },
          },
          {
            icon: 'arrow_right',
            title: 'Charging Windows',
            type: 'chips',
            APIKey: 'chargingWindows',
            value: {
              path: [''],
            },
          },
          {
            title: 'Country',
            APIKey: 'country',
            value: {
              path: ['country.name'],
            },
            link: {
              resource: 'countries',
              filter: 'country.id',
            },
            options: {
              flag: 'country.id',
            },
          },
          {
            icon: 'dns',
            title: 'Platform',
            APIKey: 'platform',
            link: {
              resource: 'platforms',
              filter: 'platform.id',
            },
            value: {
              path: ['platform.name'],
            },
          },
          {
            icon: 'code',
            title: 'Grace Interval',
            APIKey: 'graceInterval',
            value: {
              path: ['graceInterval'],
              type: 'interval',
            },
          },
          {
            icon: 'timelapse',
            title: 'Period',
            APIKey: 'period',
            value: {
              path: ['period.name'],
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'Sequence Rules',
        elements: [
          {
            resource: {
              APIKey: 'orderedsequencerules',
              config: new OrderedsequenceruleConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'General',
        elements: [
          {
            showEmpty: true,
            columns: true,
            list: [
              {
                icon: 'local_offer',
                title: 'Offers',
                APIKey: 'offers',
                link: {
                  resource: 'offers',
                  list: 'offerbillingpolicy',
                  target: 'offerbillingpolicy.billingpolicy',
                },
              },
              {
                icon: 'signal_cellular_alt',
                title: 'Enduser Provider',
                APIKey: 'enduserproviderjob',
                link: {
                  resource: 'firms',
                  filter: 'enduserproviderjob.id',
                },
                value: {
                  path: [
                    'enduserproviderjob.firm.name',
                    'enduserproviderjob.firm.headquarterscountry.id',
                    'enduserproviderjob.activity.name',
                  ],
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Functionnalities',
        elements: [
          {
            showEmpty: true,
            title: 'Grace',
            columns: true,
            list: [
              {
                title: 'Tries',
                APIKey: 'graceTries',
                value: {
                  path: ['graceTries'],
                },
              },
              {
                title: 'Interval',
                APIKey: 'graceInterval',
                value: {
                  path: ['graceInterval'],
                  type: 'interval',
                },
              },
            ],
          },
          {
            showEmpty: true,
            title: 'Reset',
            columns: true,
            list: [
              {
                title: 'Tries',
                APIKey: 'resetTries',
                value: {
                  path: ['resetTries'],
                },
              },
              {
                title: 'Interval',
                APIKey: 'resetInterval',
                value: {
                  path: ['resetInterval'],
                  type: 'interval',
                },
              },
            ],
          },
          {
            showEmpty: true,
            title: 'Cooldown',
            columns: true,
            list: [
              {
                title: 'Interval',
                APIKey: 'cooldownInterval',
                value: {
                  path: ['cooldownInterval'],
                  type: 'interval',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Actions',
        elements: [
          {
            showEmpty: true,
            columns: true,
            list: [
              {
                title: 'New Cycle',
                APIKey: 'newcycleaction',
                value: {
                  path: ['newcycleaction.name'],
                },
              },
              {
                title: 'End Policy',
                APIKey: 'endpolicyaction',
                value: {
                  path: ['endpolicyaction.name'],
                },
              },
              {
                title: 'Start Policy',
                APIKey: 'startpolicyaction',
                value: {
                  path: ['startpolicyaction.name'],
                },
              },
              {
                title: 'Reset Start',
                APIKey: 'resetstartaction',
                value: {
                  path: ['resetstartaction.name'],
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Data',
        elements: [
          {
            list: [
              {
                type: 'data',
                APIKey: 'data',
                value: {
                  path: ['data'],
                },
              },
            ],
          },
        ],
      },
    ],
  };
  wizard = {
    linear: false,
    noCloseAfterCreation: true,
    tabs: [
      {
        title: 'General',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            required: true,
            translatable: true,
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            multiple: false,
            required: true,
            search: new CountryConfig(this.atom),
            activatingSequence: [
              {
                group: 'general',
                control: 'enduserproviderjob',
                filters: {
                  filter: 'equal.firm.headquarterscountry.id',
                  fixFilter: { 'equal.activity.id': '2' },
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Enduser Provider',
            formControl: 'enduserproviderjob',
            APIKey: 'enduserproviderjob',
            multiple: false,
            required: true,
            search: new EndUserProviderJobConfig(this.atom),
            activatingSequence: [
              {
                group: 'general',
                control: 'platform',
                filters: {
                  filter: 'equal.platformcapabilities.enduserproviderjob.id',
                },
              },
            ],
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Platform',
            formControl: 'platform',
            APIKey: 'platform',
            multiple: false,
            required: true,
            search: new PlatformConfig(this.atom),
            noCols: true,
          },
          {
            type: 'boolean',
            title: 'Split Billing ?',
            formControl: 'splitBilling',
            noCols: true,
          },
          {
            type: 'resource',
            title: 'Period',
            formControl: 'period',
            required: false,
            search: new PeriodConfig(false),
          },
          {
            type: 'fixlist',
            title: 'Mode',
            formControl: 'mode',
            required: true,
            fixlistValues: [
              { code: 'windows', label: 'Windows' },
              { code: 'realtime', label: 'Real Time' },
            ],
            activatingSequence: [
              {
                group: 'general',
                control: 'chargingWindows',
                condition: { include: ['windows'] },
              },
            ],
          },
          {
            type: 'chips',
            title: 'Charging Windows',
            formControl: 'chargingWindows',
            APIKey: 'chargingWindows',
          },
        ],
      },
      {
        title: 'Functionnalities',
        formGroup: 'functionnalities',
        list: [
          {
            type: 'interval',
            title: 'Grace Interval',
            formControl: 'graceInterval',
          },
          {
            type: 'resource',
            title: 'New Cycle',
            formControl: 'newcycleaction',
            APIKey: 'newcycleaction',
            multiple: false,
            required: true,
            search: new BillingPolicyActionConfig(false, 'New Cycle'),
          },
          {
            type: 'resource',
            title: 'End Policy',
            formControl: 'endpolicyaction',
            APIKey: 'endpolicyaction',
            multiple: false,
            required: true,
            search: new BillingPolicyActionConfig(false, 'New Policy'),
          },
        ],
        advance: [
          {
            type: 'number',
            title: 'Grace Tries',
            formControl: 'graceTries',
          },
          {
            type: 'number',
            title: 'Reset Tries',
            formControl: 'resetTries',
          },
          {
            type: 'interval',
            title: 'Reset Interval',
            formControl: 'resetInterval',
          },
          {
            type: 'interval',
            title: 'Cooldown Interval',
            formControl: 'cooldownInterval',
          },
          {
            type: 'resource',
            title: 'Reset Start',
            formControl: 'resetstartaction',
            APIKey: 'resetstartaction',
            multiple: false,
            required: false,
            search: new BillingPolicyActionConfig(false, 'Start Cycle'),
          },
          {
            type: 'resource',
            title: 'Start Policy',
            formControl: 'startpolicyaction',
            APIKey: 'startpolicyaction',
            multiple: false,
            required: false,
            search: new BillingPolicyActionConfig(false, 'Start Policy'),
          },
        ],
      },
      // {
      //   title: 'Actions',
      //   formGroup: 'actions',
      //   list: [
      //   ],
      //   advance: [
      //   ],
      // },
      {
        title: 'Validity & Datas',
        formGroup: 'validity',
        list: [
          {
            type: 'validity',
            title: 'Validity',
            formControl: 'validity',
            required: false,
          },
          {
            type: 'data',
            formControl: 'data',
            title: 'data',
            required: false,
          },
        ],
      },
      {
        title: 'Sequence Rules',
        formGroup: 'ordersequencerules',
        list: [
          {
            type: 'ownResource',
            formControl: 'ordersequencerules',
            required: false,
            resource: {
              APIKey: 'ordersequencerules',
              config: new OrderedsequenceruleConfig(),
              multiple: true,
              sortable: true,
            },
          },
        ],
      },
    ],
  };
  resource = BillingPolicy;
  resourceName = 'Billing Policy';
  resourceKey = 'billingpolicies';
  resourceKeySingle = 'billingpolicy';
  includes = [
    'country',
    'period',
    'enduserproviderjob.firm.headquarterscountry',
    'enduserproviderjob.activity',
    'platform',
    'logic',
    'startpolicyaction',
    'resetstartaction',
    'newcycleaction',
    'endpolicyaction',
    'newmonthaction',
    'orderedsequencerules.currency',
    'pricepointrules.currency',
    'pricepointrules.onsuccess',
    'pricepointrules.nocredit',
    // 'offerbillingpolicy',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
    new StringFilter('country.id', 'Country ID', true),
    new StringFilter('country.name', 'Country Name'),
    new StringFilter('offerbillingpolicy.offer.name', 'Offer Name'),
    new BooleanFilter('split_billing', 'Split Billing'),
    new ListFilter(
      [
        { value: 'realtime', label: 'Realtime' },
        { value: 'windows', label: 'Windows' },
      ],
      'mode',
      'Mode'
    ),
    new NumberFilter('platform.id', 'Platform ID'),
    new StringFilter('platform.name', 'Platform Name'),
  ];

  constructor(private atom: AtomService) {}
}
